/**
 * Parts: what the program DEFINES, and how those definitions are put together.
 *
 * This screen exists because the generic `EntityList` cannot answer either
 * question. It showed definitions and usages in one flat table, differing only
 * in whether `Type` happened to be filled in, and containment - the thing a
 * decomposition IS - appeared nowhere at all.
 *
 * So there are exactly two views and deliberately no "All":
 *
 *   - **Definitions**, the default, because a definition is the unit somebody
 *     edits and reuses. It also carries every usage nothing contains, marked as
 *     an instance: a contained usage is dropped only because its definition's
 *     pane already shows it, and the design's ROOT is an uncontained usage, so
 *     dropping those would lose the top of the tree rather than tidy the list.
 *   - **Tree**, containment as `/api/design` walks it - which lends a usage the
 *     children and ports of the definition it is typed by. That is said on the
 *     screen, because nothing about the picture reveals it.
 *
 * Both views feed ONE decomposition pane, which is where the flowdown actually
 * becomes walkable: a definition, the usages inside it, and a link to the
 * definition each of those is a kind of.
 *
 * And both resolve to the DEFINITION. That is Patrick's third observation settled:
 * the screen was hit-or-miss about whether a click grabbed the usage or the
 * definition, worst for top-level subsystems, where `part gnc : GNC::'Guidance
 * Navigation and Control'` and the GNC definition were two different places to end
 * up - one of them with no attributes, no masses and no decomposition on it, and
 * nothing to say it was the wrong place. So a click on either view lands on the
 * definition and the usage contributes its MULTIPLICITY as context. The resolution
 * is the server's, read off `definition` on the detail, because `type_name` is
 * written with package short names and matching it here would be a guess dressed as
 * a fact. A usage whose type resolves to nothing keeps its own page - there is
 * nowhere else to send it - and the pane says so.
 *
 * A DOUBLE-CLICK opens the full editor at `/parts/{id}`, which is the other half of
 * observation four: the pane edits the quantities it shows, and everything else
 * about a part is the editor's.
 *
 * Both views also carry the ROLLED-UP quantities, which is what Patrick meant by
 * traceability here: a total is a property of a SUBTREE, so the tree is where it
 * belongs, and the pane is the one place that shows a part's own value beside it.
 * Which quantities appear is read off the payload and never named here - an
 * attribute rolls up because the model marks it `@EchelonSysML::RollsUp`, so a
 * program declaring timing, memory or delta-V of its own appears with no edit on
 * this side, and a hard-coded "Dry mass" column would defeat the whole design.
 *
 * `api.design()` keeps only the rows of `GET /api/design` and drops the
 * `attributes` and `unstorable` it answers beside them, so the tree's columns are
 * inferred from the KEYS of the totals - which the server fills for exactly the
 * attributes it rolled up - labelled from the registry. The pane, which fetches a
 * whole part, uses the descriptors directly. Both go through one function so the
 * two cannot drift apart in how they name a column.
 *
 * Columns come from the registry rather than from a list written here; the
 * filter and the tree rebuild live in `components/parts/parts-model.ts`, pure
 * and tested apart from anything that draws them, because a wrong answer from
 * either renders something entirely plausible - and how a total becomes text
 * lives in `components/parts/rollup-model.ts` for the same reason, only more so.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ApiError, PAGE_LIMIT, api } from '../api'
import EmptyState from '../components/common/EmptyState'
import HelpTip from '../components/common/HelpTip'
import ExportMenu from '../components/entity/ExportMenu'
import ImportCsvDialog from '../components/entity/ImportCsvDialog'
import DecompositionPane from '../components/parts/DecompositionPane'
import DefinitionsTable from '../components/parts/DefinitionsTable'
import DesignTree, { TREE_NOTE } from '../components/parts/DesignTree'
import PartWizard from '../components/parts/PartWizard'
import {
  RESOLVES_TO_DEFINITION,
  buildDesignTree,
  definitionsListing,
  pruneDesignTree,
  resolveToDefinition,
} from '../components/parts/parts-model'
import type { UsageContext } from '../components/parts/parts-model'
import { ROLLUP_NOTE, rollupColumns } from '../components/parts/rollup-model'
import { findKind } from '../schema'
import type { DesignRow, PartDetail, PartRow } from '../schema'
import { useCanWrite, useSchema } from '../state/AppData'
import EntityFilterBar from '../components/entity/EntityFilterBar'
import { matchesQuery, readSearch, writeSearch } from '../components/entity/rowFilter'
import { useRememberedFilters } from '../components/entity/rememberedFilters'

// DEBOUNCE_MS was here, and went with the server-side search: it existed to
// stop every keystroke becoming a request. Filtering happens in the browser
// now, so there is no request to space out.

export const DEFINITIONS_NOTE =
  'Every part definition, plus any usage nothing contains — the design’s root is ' +
  'one of those, so it is marked as an instance rather than dropped. A usage that ' +
  'IS contained is on its definition’s pane instead.'

export const CLICK_NOTE = RESOLVES_TO_DEFINITION

type View = 'definitions' | 'tree'

/**
 * The header note, which is now a `?` rather than a paragraph.
 *
 * A wrapper rather than `<HelpTip title={...}>` inline, because what the note
 * SAYS depends on the view and the branch reads better as the component's
 * child than as a prop. See `HelpTip` for the rule: the screen states what is
 * true, the tip explains why.
 */
function HelpTipHolder({ children }: { children: string }) {
  return <HelpTip title={children} label="How to read this listing" />
}

export default function Parts() {
  const schema = useSchema()
  const permission = useCanWrite()
  const navigate = useNavigate()
  const kind = findKind(schema, 'parts')

  const [view, setView] = useState<View>('definitions')
  /**
   * The search, in the URL under `search` as on every other list (issue #86).
   *
   * It was component state, so it was gone the moment anything else was opened:
   * a part's page and back, and the box was empty. In the URL it is a link, and
   * remembered so that the sidebar and the breadcrumb put it back. Replace, not
   * push: typing must not fill up the back button.
   */
  const [params, setParams] = useSearchParams()
  const search = readSearch(params)
  const setSearch = (next: string) =>
    setParams((now) => writeSearch(now, next), { replace: true })
  useRememberedFilters()
  const [rows, setRows] = useState<PartRow[] | null>(null)
  const [error, setError] = useState<ApiError | null>(null)

  const [design, setDesign] = useState<DesignRow[] | null>(null)
  const [designError, setDesignError] = useState<ApiError | null>(null)

  // `selected` is what the PANE shows, which is a definition whenever there is
  // one; `usage` is the placement that led there, kept apart because the two are
  // different facts - a definition and the multiplicity of one usage of it.
  const [selected, setSelected] = useState('')
  const [usage, setUsage] = useState<UsageContext | null>(null)
  const [unresolved, setUnresolved] = useState(false)
  const [detail, setDetail] = useState<PartDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<ApiError | null>(null)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [reloads, setReloads] = useState(0)
  /**
   * The attributes a LISTING can give a column to, from the model.
   *
   * Undefined until the first page arrives, and undefined forever against an
   * older server - both of which keep every column, because a table that is too
   * wide is better than one missing the figure somebody came for.
   */
  const [commonAttributes, setCommonAttributes] = useState<string[] | undefined>(undefined)
  /** What the server says exists, against what it was allowed to send. */
  const [total, setTotal] = useState(0)

  /**
   * Fetched WHOLE, then filtered here - like every other element table.
   *
   * This used to search on the SERVER, on the reasoning that filtering a paged
   * listing in the browser searches only the page in hand. That reasoning was
   * right and its premise is gone: the fetch is no longer paged below the
   * model, and the truncation notice covers the case where it is.
   *
   * It also left ONE BOX driving TWO mechanisms - the table filtered by the
   * server, the tree pruned in the browser - which is exactly the split issue
   * #35 set out to remove. `common_attributes` is computed over the whole
   * session rather than the returned page, so dropping the search does not
   * change it.
   */
  useEffect(() => {
    let live = true
    api
      .parts({ limit: PAGE_LIMIT })
      .then((page) => {
        if (!live) return
        setRows(page.items)
        setTotal(page.total)
        setCommonAttributes(page.common_attributes)
        setError(null)
      })
      .catch((failure: ApiError) => live && setError(failure))
    return () => {
      live = false
    }
  }, [reloads])

  // The tree is the whole design in one answer, so it is fetched once and
  // filtered in the browser. Its failure is kept apart from the listing's: the
  // definitions table is still useful when /api/design is the thing that broke.
  useEffect(() => {
    let live = true
    api
      .design()
      .then((items) => live && setDesign(items))
      .catch((failure: ApiError) => live && setDesignError(failure))
    return () => {
      live = false
    }
  }, [reloads])

  // Fetch what was clicked, then land on the DEFINITION it belongs to. The
  // resolution is `resolveToDefinition`, which reads the server's own answer off
  // the payload; when it points somewhere else this asks again for that part,
  // which costs one request and never guesses at a type reference.
  useEffect(() => {
    if (!selected) {
      setDetail(null)
      return
    }
    let live = true
    setDetailLoading(true)
    setDetailError(null)
    api
      .part(selected)
      .then((answer) => {
        if (!live) return
        const landing = resolveToDefinition(answer)
        setUnresolved(landing.unresolved)
        // Only ever SET, never cleared here: the second fetch lands on a
        // definition, whose landing carries no usage, and clearing on that would
        // throw away the very context the first fetch discovered. `onSelect`
        // clears it, which is where a fresh click belongs.
        if (landing.usage) setUsage(landing.usage)
        if (landing.id !== answer.id) {
          setSelected(landing.id)
          return
        }
        setDetail(answer)
      })
      .catch((failure: ApiError) => live && setDetailError(failure))
      .finally(() => live && setDetailLoading(false))
    return () => {
      live = false
    }
  }, [selected, reloads])

  /**
   * The definitions table, filtered HERE.
   *
   * The filter used to reach the server, so `rows` arrived already narrowed and
   * this line had nothing to do. Dropping the server search left the table
   * unfiltered while the tree beside it still pruned - one box, one half of it
   * working - which is worse than the split it replaced, and invisible unless
   * somebody types into it.
   *
   * Filtered BEFORE `definitionsListing`, so its "and N contained instances"
   * count describes the rows on screen rather than the whole model.
   */
  const listing = useMemo(
    () => definitionsListing((rows ?? []).filter((row) => matchesQuery(row, search))),
    [rows, search],
  )
  const tree = useMemo(() => buildDesignTree(design ?? []), [design])
  const shownTree = useMemo(() => pruneDesignTree(tree, search), [tree, search])
  // Inferred from the rows rather than from the selection, so the tree's columns
  // do not appear and disappear as somebody clicks around it.
  const rollups = useMemo(() => rollupColumns(null, design ?? [], kind), [design, kind])

  // A fresh click is a fresh question, so the usage that led anywhere before it
  // goes. Anything else leaves `[4]` from one subsystem sitting on the next.
  const onSelect = useCallback((id: string) => {
    setUsage(null)
    setUnresolved(false)
    setSelected(id)
  }, [])

  // The full editor, on a double-click from either view.
  const onOpen = useCallback(
    (id: string) => navigate(`/parts/${encodeURIComponent(id)}`),
    [navigate],
  )

  if (!kind) {
    return (
      <EmptyState
        title="This BEST does not model parts"
        description="The registry has no part kind, so there is nothing for this screen to show."
        actions={[{ label: 'Go to the dashboard', onClick: () => navigate('/') }]}
      />
    )
  }

  if (error) {
    return (
      <EmptyState
        title="Could not load the parts"
        description={error.message}
        actions={[{ label: 'Try again', onClick: () => setReloads((n) => n + 1) }]}
      />
    )
  }

  if (!rows) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const nothingHere = listing.listed.length === 0 && shownTree.length === 0

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5">Parts</Typography>
          <HelpTipHolder>
            {view === 'definitions'
              ? `${DEFINITIONS_NOTE} ${CLICK_NOTE}`
              : // The rollup legend only where the rollups are: the definitions
                // table shows what each part STATES, which needs no explaining.
                rollups.length > 0
                ? `${TREE_NOTE} ${CLICK_NOTE} ${ROLLUP_NOTE}`
                : `${TREE_NOTE} ${CLICK_NOTE}`}
          </HelpTipHolder>
        </Box>
        {/* The same round trip every other element table offers. This screen
            is not EntityList - it has tabs, a tree and a decomposition pane -
            so it does not inherit them, and issue #33 names parts first.

            A parts CSV carries one column per attribute the PROGRAM's model
            declares, not a fixed set, because the server resolves the kind
            against the session before it builds the header. */}
        <ExportMenu plural="parts" />
        <Tooltip title={permission.allowed ? '' : permission.reason} describeChild>
          <span>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setImporting(true)}
              disabled={!permission.allowed}
            >
              Import CSV
            </Button>
          </span>
        </Tooltip>
        {kind.can_create && (
          <Tooltip title={permission.allowed ? '' : permission.reason} describeChild>
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreating(true)}
                disabled={!permission.allowed}
              >
                New part definition
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Tabs value={view} onChange={(_event, next: View) => setView(next)}>
        <Tab value="definitions" label="Definitions" />
        <Tab value="tree" label="Tree" />
      </Tabs>

      <EntityFilterBar
        plural="parts"
        value={search}
        onChange={setSearch}
        hint="ancestors kept in the tree"
      />
      {total > rows.length && (
        <Typography variant="body2" color="warning.main">
          Showing the first {rows.length} of {total}. The filter searches only these.
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: selected ? 7 : 12 }}>
          <Paper sx={{ p: 1.5 }}>
            {nothingHere ? (
              <EmptyState
                title={search ? `Nothing matches “${search}”` : 'This program models no parts'}
                description={
                  search
                    ? undefined
                    : 'A part definition and a usage of it inside another would fill this ' +
                      'screen. Declare them in the model and import it again.'
                }
              />
            ) : view === 'definitions' ? (
              <DefinitionsTable
                kind={kind}
                commonAttributes={commonAttributes}
                listed={listing.listed}
                selected={selected}
                onSelect={onSelect}
                onOpen={onOpen}
              />
            ) : designError ? (
              <Alert severity="error">{designError.message}</Alert>
            ) : !design ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <DesignTree
                nodes={shownTree}
                selected={selected}
                onSelect={onSelect}
                onOpen={onOpen}
                columns={rollups}
              />
            )}

            {view === 'definitions' && listing.contained > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {listing.contained} contained{' '}
                {listing.contained === 1 ? 'usage is' : 'usages are'} not listed here — each is
                shown on the pane of the definition that declares it.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* ONLY WHEN A PART IS SELECTED. The pane is about the part in hand, so
            with nothing in hand it was a titled empty box taking two fifths of
            the screen away from the table somebody is reading to make a
            selection in the first place. The table takes the full width until
            there is something to show beside it. */}
        {selected && (
        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Decomposition
            </Typography>
            <DecompositionPane
              detail={detail}
              loading={detailLoading}
              error={detailError}
              permission={permission}
              schema={schema}
              kind={kind}
              usage={usage}
              unresolved={unresolved}
              // Re-read everything a write could have changed: the pane's own
              // detail, the listing's stated columns, and the tree's totals - a
              // mass edited here changes every total above it.
              onChanged={() => setReloads((n) => n + 1)}
            />
          </Paper>
        </Grid>
        )}
      </Grid>

      {importing && (
        <ImportCsvDialog
          kind={kind}
          onClose={() => setImporting(false)}
          onImported={() => setReloads((n) => n + 1)}
        />
      )}
      {creating && (
        // The part-def wizard rather than the generic dialog: a part is always a
        // definition, `type_name` is refused here, and what a definition
        // specializes, sits inside and contains are questions the generic form
        // cannot ask.
        <PartWizard
          kind={kind}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false)
            // Stay on this screen and select what was just made, rather than
            // navigating away: the next thing anybody does with a new definition
            // is look at what is inside it.
            setSelected(id)
            setReloads((n) => n + 1)
          }}
        />
      )}
    </Stack>
  )
}
